import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { FilterBar } from "@/components/FilterBar";
import { SavedSearchButton } from "@/components/SavedSearchButton";
import { SlidersHorizontal, Loader2 } from "lucide-react";

const PAGE_SIZE = 12;

export const Listings = ({ onAuth, forcedType = null, title, eyebrow }) => {
  const [params] = useSearchParams();
  const initial = {
    q: params.get("q") || "",
    listing_type: forcedType || params.get("listing_type") || "",
    locality: params.get("locality") || "",
    property_type: params.get("property_type") || "",
    bhk: params.get("bhk") ? Number(params.get("bhk")) : "",
  };
  const [filters, setFilters] = useState(initial);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hideSold, setHideSold] = useState(false);
  const sentinel = useRef(null);

  // Reset + first page whenever filters change
  useEffect(() => {
    setLoading(true);
    setOffset(0);
    const effective = forcedType ? { ...filters, listing_type: forcedType } : filters;
    const clean = Object.fromEntries(Object.entries(effective).filter(([, v]) => v !== "" && v != null));
    api
      .get("/properties", { params: { ...clean, paginated: true, limit: PAGE_SIZE, offset: 0 } })
      .then(({ data }) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
        setHasMore(!!data.has_more);
      })
      .finally(() => setLoading(false));
  }, [filters, forcedType]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const effective = forcedType ? { ...filters, listing_type: forcedType } : filters;
    const clean = Object.fromEntries(Object.entries(effective).filter(([, v]) => v !== "" && v != null));
    const nextOffset = offset + PAGE_SIZE;
    try {
      const { data } = await api.get("/properties", {
        params: { ...clean, paginated: true, limit: PAGE_SIZE, offset: nextOffset },
      });
      setItems((prev) => [...prev, ...(data.items || [])]);
      setOffset(nextOffset);
      setHasMore(!!data.has_more);
    } finally {
      setLoadingMore(false);
    }
  };

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!sentinel.current || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "400px 0px" },
    );
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [sentinel.current, hasMore, offset, loadingMore]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="mb-8">
        <span className="section-eyebrow">{eyebrow || "Browse"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-[var(--teak)] mt-2">{title || "All Listings in Mysuru"}</h1>
        <p className="text-[var(--ink-muted)] mt-2">
          {forcedType === "buy"
            ? "Verified homes for sale across Mysuru's hyperlocal pockets — refine by neighborhood, BHK or type."
            : forcedType === "rent"
            ? "Monthly rentals from owners and agents — filter by locality, BHK or furnishing."
            : "Buy, rent, or browse peer-to-peer — refine by neighborhood, BHK or type."}
        </p>
      </div>

      <div className="p-4 bg-white rounded-2xl border border-[var(--line)] mb-8">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-[var(--primary)]" />
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-muted)]">Filter</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label
              className="inline-flex items-center gap-2 text-xs text-[var(--teak)] cursor-pointer select-none"
              data-testid="hide-sold-toggle-label"
            >
              <input
                type="checkbox"
                checked={hideSold}
                onChange={(e) => setHideSold(e.target.checked)}
                className="h-4 w-4 rounded accent-[var(--primary)]"
                data-testid="hide-sold-toggle"
              />
              Hide sold
            </label>
            <SavedSearchButton filters={filters} onAuth={onAuth} />
          </div>
        </div>
        <FilterBar filters={filters} onChange={setFilters} hideListingType={!!forcedType} />
      </div>

      {loading ? (
        <div className="text-center py-20 text-[var(--ink-muted)]">Loading listings...</div>
      ) : (() => {
        const visibleItems = hideSold ? items.filter((p) => !p.sold) : items;
        const hiddenCount = items.length - visibleItems.length;
        if (visibleItems.length === 0) {
          return (
            <div className="text-center py-20">
              <div className="font-serif text-2xl text-[var(--teak)]">
                {hideSold && hiddenCount > 0 ? "All loaded listings are marked sold" : "No properties match your filters"}
              </div>
              <div className="text-[var(--ink-muted)] mt-2">
                {hideSold && hiddenCount > 0 ? "Uncheck 'Hide sold' or load more." : "Try widening the search."}
              </div>
            </div>
          );
        }
        return (<>
          <div className="text-sm text-[var(--ink-muted)] mb-4" data-testid="listings-count">
            Showing <span className="font-mono font-bold text-[var(--teak)]">{visibleItems.length}</span> of{" "}
            <span className="font-mono font-bold text-[var(--teak)]">{total}</span> properties
            {hideSold && hiddenCount > 0 && (
              <span className="ml-2 text-[var(--primary)]" data-testid="hidden-sold-count">· {hiddenCount} sold hidden</span>
            )}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleItems.map((p, i) => (
              <PropertyCard key={p.id} property={p} index={i} />
            ))}
          </div>

          {hasMore && (
            <div ref={sentinel} className="flex justify-center py-10">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="btn-ghost inline-flex items-center gap-2"
                data-testid="load-more-btn"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading more…
                  </>
                ) : (
                  <>Load more</>
                )}
              </button>
            </div>
          )}
          {!hasMore && visibleItems.length > PAGE_SIZE && (
            <div className="text-center py-10 text-xs text-[var(--ink-muted)]" data-testid="listings-end">
              You've reached the end of this list.
            </div>
          )}
        </>);
      })()}
    </div>
  );
};
